/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BoltIcon,
  BeakerIcon,
  Cog6ToothIcon,
  HomeIcon,
  MapPinIcon,
  PauseIcon,
  PlayIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import { getVacuum, getWaterCandidates, setVacuumControl, setVacuumPhoto, setWaterSource, vacuumAction, type VacuumControl, type VacuumInfo } from "@/api/homeassistant";
import { VacuumArt } from "./VacuumArt";
import { buzz, HAPTIC } from "./haptics";
import "./hub.css";

const STATE_LABEL: Record<string, string> = {
  cleaning: "Cleaning",
  docked: "Docked",
  charging: "Charging",
  returning: "Returning to base",
  paused: "Paused",
  idle: "Idle",
  error: "Error",
  unavailable: "Unavailable",
};

const KIND_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  suction: BoltIcon,
  water: BeakerIcon,
  mode: Cog6ToothIcon,
};

/** Big segmented switch: one tap picks an option, the choice shows immediately. */
const Segmented: React.FC<{ control: VacuumControl; value: string; onPick: (v: string) => void }> = ({ control, value, onPick }) => {
  const Icon = KIND_ICON[control.kind] ?? Cog6ToothIcon;
  return (
    <section className="hub-vac-card">
      <h3>
        <Icon />
        {control.name}
      </h3>
      <div className="hub-seg" role="radiogroup" aria-label={control.name} style={{ "--n": control.options.length } as React.CSSProperties}>
        {control.options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={value === o.value ? "on" : ""} onClick={() => onPick(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </section>
  );
};

/** Water level has no control in this Home Assistant setup: let the user point at one. */
const WaterSource: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const { data } = useQuery({ queryKey: ["hub-vacuum-candidates"], queryFn: getWaterCandidates, retry: false });
  const [saving, setSaving] = useState(false);
  return (
    <section className="hub-vac-card hub-vac-card--water">
      <h3>
        <BeakerIcon />
        Water level
      </h3>
      <p className="hub-vac-note">Home Assistant gives no water control for this vacuum. Pick the entity that sets it:</p>
      <select
        className="hub-vac-pick"
        defaultValue=""
        disabled={saving || !data}
        onChange={async (e) => {
          if (!e.target.value) return;
          setSaving(true);
          try {
            await setWaterSource(e.target.value);
            onDone();
          } finally {
            setSaving(false);
          }
        }}
      >
        <option value="">{data && data.candidates.length === 0 ? "No suitable entities" : "Choose entity…"}</option>
        {(data?.candidates ?? []).map((c) => (
          <option key={c.id} value={c.id}>{`${c.name} · ${c.domain}`}</option>
        ))}
      </select>
    </section>
  );
};

/** The vacuum's own page: built to fit one screen, even a small one. */
export const VacuumPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, error } = useQuery({ queryKey: ["hub-vacuum"], queryFn: getVacuum, refetchInterval: 3000, retry: false });
  // what the user just picked, until Home Assistant reports it back
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["hub-vacuum"] });

  const forget = (id: string) =>
    setPicked((p) => {
      const n = { ...p };
      delete n[id];
      return n;
    });

  const pick = async (info: VacuumInfo, c: VacuumControl, value: string) => {
    buzz(HAPTIC.tap);
    setPicked((p) => ({ ...p, [c.id]: value }));
    try {
      await setVacuumControl(info.entityId, c.id, value);
      buzz(HAPTIC.ok);
    } catch {
      buzz(HAPTIC.fail);
      forget(c.id);
    }
    window.setTimeout(() => {
      forget(c.id);
      refresh();
    }, 2500);
  };

  const act = async (info: VacuumInfo, action: Parameters<typeof vacuumAction>[1]) => {
    buzz(HAPTIC.tap);
    setBusy(true);
    try {
      await vacuumAction(info.entityId, action);
      buzz(HAPTIC.ok);
    } catch {
      buzz(HAPTIC.fail);
    }
    window.setTimeout(() => {
      setBusy(false);
      refresh();
    }, 800);
  };

  const cleaning = data?.state === "cleaning" || data?.state === "returning";
  const state = data ? (data.fault || data.state === "error" ? "crit" : "ok") : "off";
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadPhoto = async (file: File | null) => {
    try {
      await setVacuumPhoto(file);
      buzz(HAPTIC.ok);
    } catch {
      buzz(HAPTIC.fail);
    }
    await queryClient.invalidateQueries({ queryKey: ["vacuum-photo"] });
    refresh();
    void queryClient.invalidateQueries({ queryKey: ["hub-ha"] });
  };
  const [waterOpen, setWaterOpen] = useState(false);
  const hasWater = !!data?.controls.some((c) => c.kind === "water");

  return (
    <div className={`hub hub--${state} hub-vac`} role="main">
      <div className="hub-vac-page">
        <header className="hub-vac-head">
          <button type="button" className="hub-vac-back" onClick={() => navigate({ to: "/hub", search: { page: undefined } })} aria-label="Back">
            <ArrowLeftIcon />
          </button>
          <div className="hub-vac-title">
            <h2>{data?.name ?? "Vacuum"}</h2>
            {data && <span>{STATE_LABEL[data.state] ?? data.state}</span>}
          </div>
        </header>

        {error && <p className="hub-err">{(error as Error).message}</p>}
        {data?.fault && <p className="hub-err">{data.fault}</p>}

        {data && (
          <>
            <div className="hub-vac-hero">
              <div className={`hub-vac-tile hub-vac-tile--hero${cleaning ? " hub-vac-tile--on" : ""}`}>
                <VacuumArt photo={data.photo} cleaning={cleaning} />
                {data.battery !== undefined && (
                  <span className="hub-vac-bat" aria-label="Battery">
                    <b>{`${data.battery}%`}</b>
                    <i style={{ "--lvl": `${data.battery}%` } as React.CSSProperties} className={data.battery < 20 ? "low" : ""} />
                  </span>
                )}
                <em className="hub-vac-cta">{STATE_LABEL[data.state] ?? data.state}</em>
              </div>
              <div className="hub-vac-sec">
                {/* One clear action: what the robot can do next. Robots without a pause command are stopped instead. */}
                <button
                  type="button"
                  disabled={busy}
                  className={`hub-vac-main${cleaning ? " hub-vac-main--stop" : ""}`}
                  onClick={() => void act(data, cleaning ? (data.canPause ? "pause" : "stop") : "start")}
                >
                  {cleaning ? data.canPause ? <PauseIcon /> : <StopIcon /> : <PlayIcon />}
                  <span>{cleaning ? (data.canPause ? "Pause" : "Stop") : data.state === "paused" ? "Resume" : "Start cleaning"}</span>
                </button>
                <button type="button" disabled={busy} onClick={() => void act(data, "home")}>
                  <HomeIcon />
                  <span>Dock</span>
                </button>
                <button type="button" disabled={busy} onClick={() => void act(data, "locate")}>
                  <MapPinIcon />
                  <span>Find</span>
                </button>
              </div>
            </div>

            <div className="hub-vac-settings">
              {data.controls.map((c) => (
                <Segmented key={c.id} control={c} value={picked[c.id] ?? c.current} onPick={(v) => void pick(data, c, v)} />
              ))}
              {!hasWater && waterOpen && <WaterSource onDone={() => { setWaterOpen(false); refresh(); }} />}
            </div>

            <div className="hub-vac-foot">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => void uploadPhoto(e.target.files?.[0] ?? null)} />
              <button type="button" onClick={() => fileRef.current?.click()}>
                Change photo
              </button>
              {!hasWater && !waterOpen && (
                <button type="button" onClick={() => setWaterOpen(true)}>
                  Set up water level
                </button>
              )}
              {data.photo && (
                <button type="button" onClick={() => void uploadPhoto(null)}>
                  Remove photo
                </button>
              )}
            </div>

            {data.parts.length > 0 && (
              <div className="hub-vac-parts">
                {data.parts.map((p) => (
                  <span key={p.name} className={p.percent < 20 ? "low" : ""}>
                    {p.name}
                    <b>{`${p.percent}%`}</b>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
